import { useState, type ReactNode } from "react";
import { useRyftPayment } from "./useRyftPayment";
import { PaymentOption } from "./PaymentOptions";
import { BadgeBlue, Button, Spinner } from "@sellar-io/components";
import { RyftCardForm } from "./RyftCardForm";
import { mapToUserError } from "./mapToUserError";

type RyftCardPaymentOptionProps = {
  selected: boolean;
  visible: boolean;
  title: ReactNode;
  description: ReactNode;
  amount: number;
  supplierId: number;
  onSelect: () => void;
  onDeselect: () => void;
};

export const RyftCardPaymentOption = ({
  selected,
  visible,
  title,
  description,
  amount,
  supplierId,
  onSelect,
  onDeselect,
}: RyftCardPaymentOptionProps) => {
  const [initiationError, setInitiationError] = useState<string | null>(null);
  const {
    isInitiatingPaymentSession,
    isUpdatingPaymentSession,
    initiateCheckoutPaymentSession,
  } = useRyftPayment();

  const canDisplayLoadingState =
    isInitiatingPaymentSession || isUpdatingPaymentSession;

  const handleInitiateCheckoutPaymentSession = async () => {
    setInitiationError(null);
    const response = await initiateCheckoutPaymentSession({
      amount,
      supplierId,
    });

    if (response.error) {
      setInitiationError(mapToUserError(response.error));
    }
  };

  return (
    <PaymentOption
      selected={selected}
      visible={visible}
      onSelect={() => {
        onSelect();
        handleInitiateCheckoutPaymentSession();
      }}
      onDeselect={onDeselect}
      trigger={
        <div className="flex w-full flex-1 flex-col gap-2">
          <div className="flex w-full justify-between">
            <span className="text-start font-semibold">{title}</span>
            <BadgeBlue>Pay with Ryft</BadgeBlue>
          </div>
          <span className="text-start text-zinc-500">{description}</span>
        </div>
      }
    >
      {initiationError && (
        <div className="flex flex-col gap-2 p-4">
          <p role="alert" className="text-sm text-red-600">
            {initiationError}
          </p>
          <Button
            onClick={handleInitiateCheckoutPaymentSession}
            disabled={isInitiatingPaymentSession}
          >
            Try again
          </Button>
        </div>
      )}
      {canDisplayLoadingState && (
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      )}
      <div className="px-4 pb-4">
        <RyftCardForm />
      </div>
    </PaymentOption>
  );
};
